import { BodyKeypoint, Fibo2SceneObject, ImageDescription } from "../../../../../models/image-description";
import { vglNumber } from "../../../../../utils/vglTokens";
import { CanvasObject } from "../types";

const LAYERS = ["foreground", "midground", "background"] as const;
type LayerKey = (typeof LAYERS)[number];

export const parseImageDescription = (raw: string | ImageDescription): CanvasObject[] => {
	if (!raw) return [];

	try {
		const parsed: ImageDescription = typeof raw === "string" ? JSON.parse(raw) : raw;

		const result: CanvasObject[] = [];
		let filteredIndex = 0;

		for (const layerKey of LAYERS) {
			const objects: Fibo2SceneObject[] = parsed[layerKey] ?? [];

			objects.forEach((obj, originalIndex) => {
				const bbox = obj.bounding_boxes?.[0];
				if (!bbox) return;

				const left = vglNumber(bbox.top_left?.x);
				const top = vglNumber(bbox.top_left?.y);
				const right = vglNumber(bbox.bottom_right?.x);
				const bottom = vglNumber(bbox.bottom_right?.y);
				// A bbox we can't place is worse than none — skip the object, don't draw NaN.
				if (left === undefined || top === undefined || right === undefined || bottom === undefined) return;

				const colors = (obj.colors ?? []).flatMap((c) => {
					const r = vglNumber(c.red);
					const g = vglNumber(c.green);
					const b = vglNumber(c.blue);
					return r !== undefined && g !== undefined && b !== undefined ? [{ r, g, b }] : [];
				});

				const rawKeypoints = (obj as any).keypoints as BodyKeypoint[] | undefined;
				const parsedKeypoints = rawKeypoints?.flatMap((kp) => {
					const x = vglNumber(kp.point?.x);
					const y = vglNumber(kp.point?.y);
					return x !== undefined && y !== undefined ? [{ name: kp.name, x, y }] : [];
				});
				const keypoints = parsedKeypoints?.length ? parsedKeypoints : undefined;

				result.push({
					id: `object-${filteredIndex}`,
					originalIndex,
					layerKey: layerKey as LayerKey,
					name: obj.name,
					bbox: {
						left,
						top,
						width: right - left,
						height: bottom - top,
					},
					colors,
					...(keypoints ? { keypoints } : {}),
				});

				filteredIndex++;
			});
		}

		return result;
	} catch (error) {
		console.error("Failed to parse image description:", error);
		return [];
	}
};
